import React, { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { MapPin, Phone, Users, Bed, AlertTriangle, CheckCircle, Plus, MessageSquare, Wrench, UserPlus, Clock, Calendar, Key, Wifi } from 'lucide-react';
import { toast } from 'sonner';
import { AISummaryPanel } from '@/components/shared/AISummaryPanel';

export default function HouseOverviewTab({ house, houseId, onUpdate }) {
  const [stats, setStats] = useState({
    totalRooms: 0,
    occupiedRooms: 0,
    totalResidents: 0,
    openTasks: 0,
    openMaintenance: 0
  });
  const [recentLogs, setRecentLogs] = useState([]);
  const [upcomingShifts, setUpcomingShifts] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Quick action dialogs
  const [showLogDialog, setShowLogDialog] = useState(false);
  const [showTaskDialog, setShowTaskDialog] = useState(false);
  const [showMaintenanceDialog, setShowMaintenanceDialog] = useState(false);
  const [showVisitorDialog, setShowVisitorDialog] = useState(false);
  
  // Form states
  const [logForm, setLogForm] = useState({ title: '', entry_type: 'GENERAL', notes: '' });
  const [taskForm, setTaskForm] = useState({ title: '', description: '', priority: 'MEDIUM', due_date: '' });
  const [maintenanceForm, setMaintenanceForm] = useState({ title: '', description: '', priority: 'MEDIUM', category: 'GENERAL' });
  const [visitorForm, setVisitorForm] = useState({ visitor_name: '', relationship: '', purpose: '', contact_phone: '' });

  useEffect(() => {
    fetchStats();
    fetchRecentActivity();
  }, [houseId]);

  const fetchStats = async () => {
    try {
      const [roomsRes, allocationsRes, tasksRes, maintenanceRes] = await Promise.all([
        api.get(`/houses/${houseId}/rooms`),
        api.get(`/accommodation/by-house/${houseId}`),
        api.get(`/houses/${houseId}/tasks`),
        api.get(`/maintenance?house_id=${houseId}`)
      ]);

      const rooms = roomsRes.data.data || [];
      const allocations = allocationsRes.data.data || [];
      const activeAllocations = allocations.filter(a => a.status === 'ACTIVE');
      const tasks = tasksRes.data.data || [];
      const maintenance = maintenanceRes.data.data || [];

      setStats({
        totalRooms: rooms.length,
        occupiedRooms: rooms.filter(r => r.status === 'OCCUPIED').length,
        totalResidents: activeAllocations.length,
        openTasks: tasks.filter(t => t.status === 'PENDING').length,
        openMaintenance: maintenance.filter(m => m.status === 'OPEN').length
      });
    } catch (error) {
      toast.error('Failed to load house statistics');
    } finally {
      setLoading(false);
    }
  };

  const fetchRecentActivity = async () => {
    try {
      const [logsRes, shiftsRes] = await Promise.all([
        api.get(`/houses/${houseId}/logs`),
        api.get(`/roster/shifts?house_id=${houseId}`)
      ]);
      setRecentLogs((logsRes.data.data || []).slice(0, 3));
      
      // Get upcoming shifts (next 7 days)
      const today = new Date().toISOString().split('T')[0];
      const allShifts = shiftsRes.data.items || shiftsRes.data.data || [];
      const upcoming = allShifts.filter(s => s.date >= today).slice(0, 5);
      setUpcomingShifts(upcoming);
    } catch (error) {
      console.error('Failed to load recent activity:', error);
    }
  };

  const handleQuickLog = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/houses/${houseId}/logs`, logForm);
      toast.success('Log entry created');
      setShowLogDialog(false);
      setLogForm({ title: '', entry_type: 'GENERAL', notes: '' });
      fetchRecentActivity();
    } catch (error) {
      toast.error('Failed to create log');
    }
  };

  const handleQuickTask = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/houses/${houseId}/tasks`, taskForm);
      toast.success('Task created');
      setShowTaskDialog(false);
      setTaskForm({ title: '', description: '', priority: 'MEDIUM', due_date: '' });
      fetchStats();
    } catch (error) {
      toast.error('Failed to create task');
    }
  };

  const handleQuickMaintenance = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/maintenance`, { ...maintenanceForm, house_id: houseId });
      toast.success('Maintenance request created');
      setShowMaintenanceDialog(false);
      setMaintenanceForm({ title: '', description: '', priority: 'MEDIUM', category: 'GENERAL' });
      fetchStats();
    } catch (error) {
      toast.error('Failed to create maintenance request');
    }
  };

  const handleVisitorLog = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/houses/${houseId}/logs`, {
        title: `Visitor: ${visitorForm.visitor_name}`,
        entry_type: 'VISITOR',
        notes: `Relationship: ${visitorForm.relationship}\nPurpose: ${visitorForm.purpose}\nContact: ${visitorForm.contact_phone || 'N/A'}`
      });
      toast.success('Visitor logged');
      setShowVisitorDialog(false);
      setVisitorForm({ visitor_name: '', relationship: '', purpose: '', contact_phone: '' });
      fetchRecentActivity();
    } catch (error) {
      toast.error('Failed to log visitor');
    }
  };

  const getLogTypeBadge = (type) => {
    const colors = {
      INFO: 'bg-blue-100 text-blue-700',
      INCIDENT: 'bg-red-100 text-red-700',
      VISITOR: 'bg-purple-100 text-purple-700',
      GENERAL: 'bg-gray-100 text-gray-700',
      MAINTENANCE: 'bg-orange-100 text-orange-700'
    };
    return <Badge className={colors[type] || colors.GENERAL}>{type}</Badge>;
  };

  if (loading) {
    return <div className="flex items-center justify-center py-12"><div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" /></div>;
  }

  return (
    <div className="space-y-6">
      {/* Stats Grid */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Rooms</CardTitle>
            <Bed className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.totalRooms}</div>
            <p className="text-xs text-muted-foreground">{stats.occupiedRooms} occupied</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Current Residents</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.totalResidents}</div>
            <p className="text-xs text-muted-foreground">of {house.capacity} capacity</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Open Tasks</CardTitle>
            <CheckCircle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.openTasks}</div>
            <p className="text-xs text-muted-foreground">Pending completion</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Maintenance Issues</CardTitle>
            <AlertTriangle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.openMaintenance}</div>
            <p className="text-xs text-muted-foreground">Open issues</p>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Quick Actions</CardTitle>
          <CardDescription>Common tasks for this house</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" onClick={() => setShowLogDialog(true)} data-testid="quick-add-log">
              <MessageSquare className="h-4 w-4 mr-2" /> Add Log Entry
            </Button>
            <Button variant="outline" size="sm" onClick={() => setShowTaskDialog(true)} data-testid="quick-add-task">
              <Plus className="h-4 w-4 mr-2" /> Create Task
            </Button>
            <Button variant="outline" size="sm" onClick={() => setShowMaintenanceDialog(true)} data-testid="quick-add-maintenance">
              <Wrench className="h-4 w-4 mr-2" /> Report Issue
            </Button>
            <Button variant="outline" size="sm" onClick={() => setShowVisitorDialog(true)} data-testid="quick-log-visitor">
              <UserPlus className="h-4 w-4 mr-2" /> Log Visitor
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* House Details & Recent Activity */}
      <div className="grid gap-4 lg:grid-cols-3">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">House Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <MapPin className="h-4 w-4" />
                <span className="font-medium">Address</span>
              </div>
              <p className="text-sm">{house.address || 'Not specified'}</p>
              {house.suburb && <p className="text-sm text-muted-foreground">{house.suburb} {house.postcode}</p>}
            </div>
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Key className="h-4 w-4" />
                <span className="font-medium">Access &amp; keys</span>
              </div>
              {(house.access_front_door_code || house.access_lockbox || house.access_spare_key || house.access_alarm_code) ? (
                <div className="space-y-1" data-testid="house-access-info">
                  {house.access_front_door_code && <p className="text-sm"><span className="text-muted-foreground">Front door code: </span>{house.access_front_door_code}</p>}
                  {house.access_lockbox && <p className="text-sm"><span className="text-muted-foreground">Lockbox: </span>{house.access_lockbox}</p>}
                  {house.access_spare_key && <p className="text-sm"><span className="text-muted-foreground">Spare key: </span>{house.access_spare_key}</p>}
                  {house.access_alarm_code && <p className="text-sm"><span className="text-muted-foreground">Alarm code: </span>{house.access_alarm_code}</p>}
                </div>
              ) : <p className="text-sm text-muted-foreground">Not specified</p>}
            </div>
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Wifi className="h-4 w-4" />
                <span className="font-medium">Wifi &amp; internet</span>
              </div>
              {(house.wifi_network || house.wifi_password) ? (
                <div className="space-y-1" data-testid="house-wifi-info">
                  {house.wifi_network && <p className="text-sm"><span className="text-muted-foreground">Network name: </span>{house.wifi_network}</p>}
                  {house.wifi_password && <p className="text-sm"><span className="text-muted-foreground">Wifi password: </span>{house.wifi_password}</p>}
                </div>
              ) : <p className="text-sm text-muted-foreground">Not specified</p>}
            </div>
            {Array.isArray(house.important_contacts) && house.important_contacts.length > 0 && (
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Phone className="h-4 w-4" />
                  <span className="font-medium">Important contacts</span>
                </div>
                <div className="space-y-1" data-testid="house-important-contacts">
                  {house.important_contacts.map((c, i) => (
                    <p key={i} className="text-sm">{[c.role, c.name, c.phone].filter(Boolean).join(' · ') || 'Contact'}</p>
                  ))}
                </div>
              </div>
            )}
            {house.accessible && (
              <Badge variant="outline" className="bg-green-50 text-green-700">Wheelchair Accessible</Badge>
            )}
          </CardContent>
        </Card>

        {/* Recent Logs */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Clock className="h-4 w-4" /> Recent Activity
            </CardTitle>
          </CardHeader>
          <CardContent>
            {recentLogs.length === 0 ? (
              <p className="text-sm text-muted-foreground">No recent logs</p>
            ) : (
              <div className="space-y-3">
                {recentLogs.map((log) => (
                  <div key={log.id} className="border-b pb-2 last:border-0">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">{log.title}</span>
                      {getLogTypeBadge(log.entry_type)}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      {new Date(log.created_at).toLocaleDateString()} - {log.created_by_name || 'System'}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* AI Summary */}
        <AISummaryPanel 
          summaryType="HOUSE" 
          entityId={houseId}
          entityType="HOUSE"
          title="AI Summary"
        />
      </div>

      {/* Upcoming Shifts */}
      {upcomingShifts.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Calendar className="h-4 w-4" /> Upcoming Shifts
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-2 md:grid-cols-2 lg:grid-cols-3">
              {upcomingShifts.map((shift) => (
                <div key={shift.id} className="p-3 border rounded-lg">
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-sm">{shift.shift_type || 'Shift'}</span>
                    <Badge variant={shift.status === 'OPEN' ? 'destructive' : 'secondary'} className="text-xs">
                      {shift.status}
                    </Badge>
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">
                    {shift.date} • {shift.start_time} - {shift.end_time}
                  </p>
                  {shift.required_staff && (
                    <p className="text-xs text-muted-foreground">
                      {shift.assigned_staff_ids?.length || 0}/{shift.required_staff} staff assigned
                    </p>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Quick Log Dialog */}
      <Dialog open={showLogDialog} onOpenChange={setShowLogDialog}>
        <DialogContent>
          <form onSubmit={handleQuickLog}>
            <DialogHeader>
              <DialogTitle>Add Log Entry</DialogTitle>
              <DialogDescription>Create a quick communication log entry</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Title *</Label>
                <Input value={logForm.title} onChange={(e) => setLogForm({...logForm, title: e.target.value})} required />
              </div>
              <div className="space-y-2">
                <Label>Type</Label>
                <Select value={logForm.entry_type} onValueChange={(v) => setLogForm({...logForm, entry_type: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="GENERAL">General</SelectItem>
                    <SelectItem value="INFO">Info</SelectItem>
                    <SelectItem value="INCIDENT">Incident</SelectItem>
                    <SelectItem value="MAINTENANCE">Maintenance</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Notes</Label>
                <Textarea value={logForm.notes} onChange={(e) => setLogForm({...logForm, notes: e.target.value})} rows={3} />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowLogDialog(false)}>Cancel</Button>
              <Button type="submit">Create Log</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Quick Task Dialog */}
      <Dialog open={showTaskDialog} onOpenChange={setShowTaskDialog}>
        <DialogContent>
          <form onSubmit={handleQuickTask}>
            <DialogHeader>
              <DialogTitle>Create Task</DialogTitle>
              <DialogDescription>Add a new task for this house</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Title *</Label>
                <Input value={taskForm.title} onChange={(e) => setTaskForm({...taskForm, title: e.target.value})} required />
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea value={taskForm.description} onChange={(e) => setTaskForm({...taskForm, description: e.target.value})} rows={2} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Priority</Label>
                  <Select value={taskForm.priority} onValueChange={(v) => setTaskForm({...taskForm, priority: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="LOW">Low</SelectItem>
                      <SelectItem value="MEDIUM">Medium</SelectItem>
                      <SelectItem value="HIGH">High</SelectItem>
                      <SelectItem value="CRITICAL">Critical</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Due Date</Label>
                  <Input type="date" value={taskForm.due_date} onChange={(e) => setTaskForm({...taskForm, due_date: e.target.value})} />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowTaskDialog(false)}>Cancel</Button>
              <Button type="submit">Create Task</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Quick Maintenance Dialog */}
      <Dialog open={showMaintenanceDialog} onOpenChange={setShowMaintenanceDialog}>
        <DialogContent>
          <form onSubmit={handleQuickMaintenance}>
            <DialogHeader>
              <DialogTitle>Report Maintenance Issue</DialogTitle>
              <DialogDescription>Log a maintenance request for this house</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Issue Title *</Label>
                <Input value={maintenanceForm.title} onChange={(e) => setMaintenanceForm({...maintenanceForm, title: e.target.value})} required placeholder="e.g., Broken door handle" />
              </div>
              <div className="space-y-2">
                <Label>Description *</Label>
                <Textarea value={maintenanceForm.description} onChange={(e) => setMaintenanceForm({...maintenanceForm, description: e.target.value})} rows={3} required placeholder="Describe the issue in detail..." />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Priority</Label>
                  <Select value={maintenanceForm.priority} onValueChange={(v) => setMaintenanceForm({...maintenanceForm, priority: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="LOW">Low</SelectItem>
                      <SelectItem value="MEDIUM">Medium</SelectItem>
                      <SelectItem value="HIGH">High</SelectItem>
                      <SelectItem value="CRITICAL">Critical</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Category</Label>
                  <Select value={maintenanceForm.category} onValueChange={(v) => setMaintenanceForm({...maintenanceForm, category: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="GENERAL">General</SelectItem>
                      <SelectItem value="PLUMBING">Plumbing</SelectItem>
                      <SelectItem value="ELECTRICAL">Electrical</SelectItem>
                      <SelectItem value="HVAC">HVAC</SelectItem>
                      <SelectItem value="STRUCTURAL">Structural</SelectItem>
                      <SelectItem value="APPLIANCE">Appliance</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowMaintenanceDialog(false)}>Cancel</Button>
              <Button type="submit">Report Issue</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Visitor Log Dialog */}
      <Dialog open={showVisitorDialog} onOpenChange={setShowVisitorDialog}>
        <DialogContent>
          <form onSubmit={handleVisitorLog}>
            <DialogHeader>
              <DialogTitle>Log Visitor</DialogTitle>
              <DialogDescription>Record a visitor to the house</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Visitor Name *</Label>
                <Input value={visitorForm.visitor_name} onChange={(e) => setVisitorForm({...visitorForm, visitor_name: e.target.value})} required placeholder="Full name" />
              </div>
              <div className="space-y-2">
                <Label>Relationship to Resident *</Label>
                <Input value={visitorForm.relationship} onChange={(e) => setVisitorForm({...visitorForm, relationship: e.target.value})} required placeholder="e.g., Family member, Friend, Support Coordinator" />
              </div>
              <div className="space-y-2">
                <Label>Purpose of Visit *</Label>
                <Input value={visitorForm.purpose} onChange={(e) => setVisitorForm({...visitorForm, purpose: e.target.value})} required placeholder="e.g., Social visit, Review meeting" />
              </div>
              <div className="space-y-2">
                <Label>Contact Phone (optional)</Label>
                <Input value={visitorForm.contact_phone} onChange={(e) => setVisitorForm({...visitorForm, contact_phone: e.target.value})} placeholder="0400 000 000" />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowVisitorDialog(false)}>Cancel</Button>
              <Button type="submit">Log Visitor</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
